import { CoreEvent, eventNames, useCoreEvents } from '@carvana/core-events';
import { useRouter } from 'next/navigation';
import { useCallback, useContext, useEffect } from 'react';
import { useIsNative } from '~/context/common/DeviceContext';
import { useUserLocation } from '~/context/common/UserContext';
import { useIsComposerReady } from '~/context/composer/ComposerContext';
import ModalContext from '~/context/vdp/ModalContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import useIsInExclusivePreview from '~/hooks/vdp/selectors/useIsInExclusivePreview';
import usePrimaryCtaButtonState from '~/hooks/vdp/selectors/usePrimaryCtaButtonState';
import buildVdpSearchRedirectUrl from '~/utilities/composer/buildVdpSearchRedirectUrl';
import parseComposerFilters from '~/utilities/composer/parseComposerFilters';
import { markSpaNavigationStart } from '~/utilities/navigationTiming';
import deriveVehicleDescription from '~/utilities/vdp/deriveVehicleDescription';

export default function useComposerEvents() {
  const { setComposerUiModalOpen } = useContext(ModalContext);
  const isComposerReady = useIsComposerReady();
  const isNative = useIsNative();
  const router = useRouter();
  const { zip } = useUserLocation();
  const {
    bodyType: bodyStyles,
    fuelDescription: fuelTypes,
    make,
    model,
    trim,
    vehicleId,
    year
  } = useGetVehicleDetails();
  const { vehicleAvailability } = useContext(UserVehicleContext);

  const {
    isReservedByOtherAndSubscribed,
    isReservedByUserAndKicked,
    type: ctaType
  } = usePrimaryCtaButtonState();
  const {
    isSold,
    notAvailableInCA,
    outOfMarket,
    outOfMarketNewCar,
    purchasePending,
    stopSaleRecall
  } = useAlertStatus();
  const isInExclusivePreview = useIsInExclusivePreview();

  const isOutOfArea = notAvailableInCA || outOfMarket || outOfMarketNewCar;
  const { description: vehicleStateDescription, scenario: vehicleState } =
    vehicleAvailability.isLoaded
      ? deriveVehicleDescription({
          ctaType,
          isInExclusivePreview,
          isOutOfArea,
          isReservedByOtherAndSubscribed,
          isReservedByUserAndKicked,
          isSold,
          purchasePending,
          stopSaleRecall
        })
      : { description: undefined, scenario: undefined };

  useCoreEvents(
    eventNames.composer.mfe.opened,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(true);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.closed,
    useCallback(
      ({ source }) => {
        if (source !== 'composer') return;

        setComposerUiModalOpen(false);
      },
      [setComposerUiModalOpen]
    )
  );
  useCoreEvents(
    eventNames.composer.mfe.updateSearch,
    useCallback(
      ({ filters, source }) => {
        if (source !== 'composer' || isNative) return;

        const validatedFilters = parseComposerFilters(filters);
        if (!validatedFilters) return;

        markSpaNavigationStart('navigate');
        router.push(buildVdpSearchRedirectUrl(validatedFilters));
      },
      [isNative, router]
    )
  );

  useEffect(() => {
    if (!isComposerReady || !vehicleId) return;

    CoreEvent.emit(eventNames.composer.pageContext, {
      context: {
        capabilities: ['update_search'],
        ...(bodyStyles && { bodyStyles }),
        ...(fuelTypes && { fuelTypes }),
        ...(make && { make }),
        ...(model && { model }),
        ...(trim && { trim }),
        url: window.location.href,
        vehicleId,
        ...(vehicleState && { vehicleState }),
        ...(vehicleStateDescription && { vehicleStateDescription }),
        ...(year && { year }),
        zip5: zip
      },
      source: 'page',
      surface: 'vdp'
    });
  }, [
    bodyStyles,
    fuelTypes,
    isComposerReady,
    make,
    model,
    trim,
    vehicleId,
    vehicleState,
    vehicleStateDescription,
    year,
    zip
  ]);
}
